import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, Space, Steps, Switch, Tabs, message, Card } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, ThunderboltOutlined, CheckOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import useAuth from '../../hooks/useAuth';
import { useBot, useCalendars } from '../../hooks/useWpbot';
import wpbotService from '../../services/wpbotService';
import BotAppsPanel from './BotAppsPanel';
import BotCalendarFields from './BotCalendarFields';
import BotPersonalityOnboarding, {
  DEFAULT_JOBS,
  PERSONA_SUMMARY_STEP,
  assemblePurpose,
  hydratePersonaFromPurpose,
} from './BotPersonalityOnboarding';
import { DEFAULT_TIMEZONE, LATAM_TIMEZONES } from '../../config/const';
import { CALENDAR_APP_KEY } from '../../config/botApps';
import {
  calendarFirestoreUid,
  calendarServices,
  findCalendar,
  sanitizeCustomEvents,
} from '../../utils/calendars';

const CREATE_STEPS = [
  { key: 'negocio', title: 'Negocio' },
  { key: 'canal', title: 'Canal' },
  { key: 'persona', title: 'Personalidad' },
  { key: 'saludo', title: 'Saludo' },
  { key: 'apps', title: 'Aplicaciones' },
]

const EDIT_STEPS = CREATE_STEPS.filter((s) => s.key !== 'apps')

/**
 * Formulario de creación/edición de bot. Onboarding por pasos (negocio →
 * canal → personalidad → saludo → aplicaciones). El dueño describe el
 * negocio y qué debe hacer el bot; el system prompt operativo no se muestra.
 * En el alta, el calendario y los eventos personalizados se configuran en
 * Aplicaciones. En edición viven en la pestaña del mismo nombre.
 */
const BotForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const { config } = useConfig();
  const { company } = useCompany();
  const { user } = useUser();
  const history = useHistory();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [generatingWelcome, setGeneratingWelcome] = useState(false);
  const [welcomeVariants, setWelcomeVariants] = useState<string[]>([]);
  const [selectedWelcome, setSelectedWelcome] = useState<number | null>(null);
  const [step, setStep] = useState(0);
  const [personaQuiz, setPersonaQuiz] = useState(0);
  const [personaSeenSummary, setPersonaSeenSummary] = useState(false);
  const STEPS = isEdit ? EDIT_STEPS : CREATE_STEPS
  const currentKey = STEPS[Math.min(step, STEPS.length - 1)]?.key
  const watchedName = Form.useWatch('display_name', form)
  const watchedBusiness = Form.useWatch('business_type', form)
  const calendarIdWatch: string | undefined = Form.useWatch('calendar_id', form)
  const jobsWatch = Form.useWatch('persona_jobs', form)

  const { data: botData, isLoading: loadingBot } = useBot(tokenData, { id, company: company.current?.uid });
  const { data: calendarsData, isLoading: loadingCalendars } = useCalendars(tokenData, { company: company.current?.uid });
  const calendars = calendarsData?.status === 200 ? (calendarsData?.calendarList || []) : [];

  const canSave = user.permissions.actions.join().includes(isEdit ? 'wpbot.action.bot.update' : 'wpbot.action.bot.create')

  useEffect(() => {
    if (isEdit && botData?.status === 200 && botData?.id) {
      const hydrated = hydratePersonaFromPurpose(botData.purpose || "")
      form.setFieldsValue({
        display_name: botData.display_name,
        phone_number: botData.phone_number,
        business_type: botData.business_type,
        timezone: botData.timezone || DEFAULT_TIMEZONE,
        is_active: botData.is_active,
        welcome_message: botData.welcome_message || "",
        tone: botData.tone || "cercano",
        purpose: botData.purpose || "",
        persona_jobs: hydrated.persona_jobs,
        persona_avoid: hydrated.persona_avoid,
        persona_avoid_extra: hydrated.persona_avoid_extra,
      })
      if (botData.purpose) {
        setPersonaQuiz(PERSONA_SUMMARY_STEP)
        setPersonaSeenSummary(true)
      }
    }
  }, [isEdit, botData, form])

  const onCalendarChange = (value: string | undefined) => {
    const cal = findCalendar(calendars, value)
    form.setFieldsValue({
      customEvents: calendarServices(cal),
      enable_calendar: Boolean(value),
    })
  }

  const persistCreatedCalendar = async (
    botId: string,
    calendarId: string,
    customEvents: unknown,
    enableCalendar: boolean,
  ) => {
    const cal = findCalendar(calendars, calendarId)
    const uidCalendar = cal ? calendarFirestoreUid(cal) : calendarId
    const events = sanitizeCustomEvents(Array.isArray(customEvents) ? customEvents : [])
    const botRes = await wpbotService.updateBot(tokenData, {
      id: botId,
      calendar_id: calendarId,
      company: company.current?.uid,
    })
    if (botRes?.error) return botRes
    const calRes = await wpbotService.updateCalendarCustomEvents(tokenData, {
      company: company.current?.uid,
      uidCalendar,
      calendar_id: uidCalendar,
      customEvents: events,
    })
    if (calRes?.error) return calRes
    if (!enableCalendar) return calRes
    return wpbotService.setAppEnabled(tokenData, {
      bot_id: botId,
      app_key: CALENDAR_APP_KEY,
      is_enabled: true,
      company: company.current?.uid,
    })
  }

  const handleSubmit = async (values: any) => {
    setSaving(true)
    const calendarId = String(values.calendar_id || botData?.calendar_id || "").trim()
    const cal = findCalendar(calendars, calendarId)
    const fromForm = sanitizeCustomEvents(values.customEvents)
    const services = fromForm.length ? fromForm : calendarServices(cal)
    const purpose = assemblePurpose(values) || values.purpose || ""
    const gen = await wpbotService.generateSystemPrompt(tokenData, {
      display_name: values.display_name || "",
      business_type: values.business_type || "",
      timezone: values.timezone || DEFAULT_TIMEZONE,
      tone: values.tone || "cercano",
      purpose,
      app_keys: calendarId ? ['calendar'] : [],
      services,
      company: company.current?.uid,
    })
    const generated = !gen?.error && gen?.system_prompt ? String(gen.system_prompt).trim() : ""
    const fallback = [
      `Eres ${String(values.display_name || "el asistente").trim()}, asistente de WhatsApp.`,
      values.business_type ? `Atiendes un negocio de ${values.business_type}.` : "",
      `Tono: ${values.tone || "cercano"}.`,
      purpose ? `El dueño quiere que: ${purpose}.` : "",
      "Hablas en español, breve y natural. No mencionas tools ni JSON.",
    ].filter(Boolean).join(" ")

    const {
      customEvents,
      enable_calendar,
      persona_jobs,
      persona_avoid,
      persona_avoid_extra,
      ...formValues
    } = values
    const payload: Record<string, unknown> = {
      ...formValues,
      phone_number: String(formValues.phone_number || "").replace(/[^\d]/g, ""),
      company: company.current?.uid,
      tone: values.tone || "cercano",
      purpose,
      system_prompt: generated || (isEdit ? (botData?.system_prompt || fallback) : fallback),
    }
    if (calendarId) payload.calendar_id = calendarId
    // PENDIENTE: selector de modelo por bot. Hoy el driver asigna
    // OPENROUTER_MODEL; no mandar un slug fijo desde el formulario.
    const res = isEdit
      ? await wpbotService.updateBot(tokenData, { ...payload, id })
      : await wpbotService.createBot(tokenData, payload)

    if (res?.error) {
      setSaving(false)
      message.error(res.message)
      return
    }
    if (!isEdit && res?.id) {
      if (calendarId) {
        const appsRes = await persistCreatedCalendar(
          String(res.id),
          calendarId,
          customEvents,
          enable_calendar !== false,
        )
        setSaving(false)
        if (appsRes?.error) {
          message.warning("Bot creado, pero no se pudo guardar el calendario. Configúralo en Aplicaciones.")
          history.push(`/bot/${res.id}`)
          return
        }
        message.success("Bot creado con calendario y servicios.")
        history.push(`/bot/${res.id}`)
        return
      }
      setSaving(false)
      message.success("Bot creado. Puedes configurar el calendario en la pestaña Aplicaciones.")
      history.push(`/bot/${res.id}`)
      return
    }
    setSaving(false)
    message.success("Bot actualizado")
    history.push('/bots')
  }

  const runGenerateWelcome = async () => {
    const values = form.getFieldsValue()
    const displayName = (values.display_name || "").trim()
    if (!displayName) {
      message.warning("Escribe el nombre del bot antes de generar el saludo")
      return
    }
    setGeneratingWelcome(true)
    const res = await wpbotService.generateWelcomeMessages(tokenData, {
      display_name: displayName,
      business_type: values.business_type || "",
      tone: values.tone || "",
      draft: values.welcome_message || "",
      company: company.current?.uid,
    })
    setGeneratingWelcome(false)
    if (res?.error) {
      message.error(res.message)
      return
    }
    const variants = Array.isArray(res?.variants)
      ? res.variants.map((v: unknown) => String(v || "").trim()).filter(Boolean).slice(0, 3)
      : []
    if (!variants.length) {
      message.error("La IA no devolvió saludos")
      return
    }
    setWelcomeVariants(variants)
    setSelectedWelcome(null)
    message.success("Elige una de las tres opciones o sigue editando el texto.")
  }

  const pickWelcome = (text: string, index: number) => {
    form.setFieldsValue({ welcome_message: text })
    setSelectedWelcome(index)
  }

  const insertWelcomeToken = (token: string) => {
    const current = String(form.getFieldValue('welcome_message') || '')
    const spacer = current && !current.endsWith(' ') && !current.endsWith('\n') ? ' ' : ''
    form.setFieldsValue({ welcome_message: current + spacer + token })
  }

  const syncPurpose = () => {
    const values = form.getFieldsValue()
    form.setFieldsValue({ purpose: assemblePurpose(values) || values.purpose || "" })
  }

  const goBack = () => {
    if (currentKey === 'persona') {
      if (personaSeenSummary && personaQuiz < PERSONA_SUMMARY_STEP) {
        setPersonaQuiz(PERSONA_SUMMARY_STEP)
        return
      }
      if (personaQuiz > 0) {
        setPersonaQuiz((q) => q - 1)
        return
      }
    }
    setStep((s) => Math.max(0, s - 1))
  }

  const goNext = async () => {
    const fieldsByKey: Record<string, string[]> = {
      negocio: ['display_name', 'business_type'],
      canal: ['phone_number'],
      persona: [],
      saludo: ['welcome_message'],
      apps: [],
    }
    const names = fieldsByKey[currentKey] || []
    if (names.length) {
      try {
        await form.validateFields(names)
      } catch {
        return
      }
    }
    if (currentKey === 'persona') {
      if (personaQuiz === 0 && !form.getFieldValue('tone')) {
        message.warning("Elige cómo debe hablar el bot")
        return
      }
      if (personaQuiz === 1) {
        const jobs = form.getFieldValue('persona_jobs') || []
        if (!Array.isArray(jobs) || !jobs.length) {
          message.warning("Marca al menos una cosa que el bot sepa hacer")
          return
        }
      }
      if (personaQuiz < PERSONA_SUMMARY_STEP) {
        if (personaQuiz === 2 || personaSeenSummary) syncPurpose()
        if (personaSeenSummary) {
          setPersonaQuiz(PERSONA_SUMMARY_STEP)
          return
        }
        const nextQuiz = personaQuiz + 1
        if (nextQuiz === PERSONA_SUMMARY_STEP) setPersonaSeenSummary(true)
        setPersonaQuiz(nextQuiz)
        return
      }
      syncPurpose()
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1))
  }

  const formContent = (
    <Card loading={isEdit && loadingBot}>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 840 }}
        initialValues={{
          timezone: DEFAULT_TIMEZONE,
          is_active: true,
          tone: 'cercano',
          purpose: '',
          persona_jobs: DEFAULT_JOBS,
          persona_avoid: [],
          persona_avoid_extra: '',
          customEvents: [],
          enable_calendar: true,
        }}
        onFinish={handleSubmit}
        disabled={!canSave}
      >
        <Steps
          current={Math.min(step, STEPS.length - 1)}
          items={STEPS.map((s) => ({ title: s.title }))}
          style={{ marginBottom: 28 }}
          size="small"
        />

        <div style={{ display: currentKey === 'negocio' ? 'block' : 'none' }}>
          <p style={{ color: '#595959', marginBottom: 16 }}>
            Cuéntale al bot para qué negocio va a atender. La forma de hablar la armas en Personalidad.
          </p>
          <Form.Item
            label="Nombre del bot"
            name="display_name"
            rules={[{ required: true, message: 'El nombre es obligatorio' }]}
          >
            <Input placeholder="Ej: Asistente de la barbería" maxLength={120} />
          </Form.Item>

          <Form.Item
            label="¿Para qué vas a usar el bot?"
            name="business_type"
            extra="Barbería, estética, consultorio, taller, restaurante… el cliente define el rubro."
            rules={[{ required: true, message: 'Indica el tipo de negocio' }]}
          >
            <Input placeholder="Ej: Barbería, clínica dental, spa..." maxLength={120} />
          </Form.Item>
        </div>

        <div style={{ display: currentKey === 'canal' ? 'block' : 'none' }}>
          <Form.Item
            label="Número de teléfono (WhatsApp)"
            name="phone_number"
            extra="El número Infobip del bot (campo 'to' del webhook), solo dígitos, sin +."
            rules={[{ required: true, message: 'El teléfono es obligatorio' }]}
          >
            <Input placeholder="Ej: 447860088970" maxLength={20} />
          </Form.Item>

          <Form.Item label="Zona horaria" name="timezone">
            <Select
              showSearch
              options={LATAM_TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
            />
          </Form.Item>
        </div>

        <div style={{ display: currentKey === 'persona' ? 'block' : 'none' }}>
          <BotPersonalityOnboarding
            form={form}
            quizStep={personaQuiz}
            disabled={!canSave}
            onJump={setPersonaQuiz}
          />
        </div>

        <div style={{ display: currentKey === 'saludo' ? 'block' : 'none' }}>
          <p style={{ color: '#595959', marginBottom: 16 }}>
            Este es el primer mensaje que recibe el cliente cuando escribe, y también cuando vuelve después de un rato.
            Escribe un borrador y mejóralo con IA: te damos tres opciones para elegir.
          </p>
          <Form.Item
            label="Saludo inicial"
            name="welcome_message"
            extra="El bot envía solo este texto. {{name}} y {{lastname}} se reemplazan por el nombre y apellido del contacto cuando ya lo conoce."
            rules={[{ required: true, message: 'El saludo inicial es obligatorio' }]}
          >
            <Input.TextArea
              rows={4}
              maxLength={500}
              showCount
              placeholder={
                watchedName || watchedBusiness
                  ? `Hola {{name}} 👋 soy ${watchedName || 'el asistente'} de ${watchedBusiness || 'nuestro negocio'}. ¿En qué te ayudo?`
                  : 'Hola {{name}} 👋 ¿En qué te ayudo?'
              }
            />
          </Form.Item>
          <Form.Item>
            <Space wrap>
              <Button htmlType="button" onClick={() => insertWelcomeToken('{{name}}')} disabled={!canSave}>
                {'{{name}}'}
              </Button>
              <Button htmlType="button" onClick={() => insertWelcomeToken('{{lastname}}')} disabled={!canSave}>
                {'{{lastname}}'}
              </Button>
              <Button
                htmlType="button"
                icon={<ThunderboltOutlined />}
                onClick={runGenerateWelcome}
                loading={generatingWelcome}
                disabled={!canSave}
              >
                Mejorar con IA
              </Button>
            </Space>
          </Form.Item>
          {welcomeVariants.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                marginBottom: 16,
              }}
            >
              {welcomeVariants.map((text, index) => {
                const selected = selectedWelcome === index
                return (
                  <Card
                    key={index}
                    hoverable
                    size="small"
                    onClick={() => pickWelcome(text, index)}
                    style={{
                      borderColor: selected ? '#1677ff' : undefined,
                      boxShadow: selected ? '0 0 0 1px #1677ff' : undefined,
                      cursor: 'pointer',
                    }}
                    title={selected ? <><CheckOutlined /> Opción {index + 1}</> : `Opción ${index + 1}`}
                  >
                    <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
                  </Card>
                )
              })}
            </div>
          )}
        </div>

        <div style={{ display: currentKey === 'apps' ? 'block' : 'none' }}>
          <p style={{ color: '#595959', marginBottom: 16 }}>
            Elige el calendario y crea los tipos de cita (eventos personalizados) que el bot va a ofrecer.
            Si no eliges uno ahora, lo puedes hacer después en la ficha del bot.
          </p>
          {!calendarIdWatch && Array.isArray(jobsWatch) && jobsWatch.length > 0 && (
            <p style={{ color: '#d46b08', marginBottom: 16 }}>
              Elegiste que el bot agende o consulte citas. Sin calendario no puede hacerlo: selecciónalo acá o actívalo después en Aplicaciones.
            </p>
          )}
          <BotCalendarFields
            calendars={calendars}
            loading={loadingCalendars}
            calendarId={calendarIdWatch}
            onCalendarChange={onCalendarChange}
          />
          {calendarIdWatch && (
            <Form.Item
              label="Activar agendamiento"
              name="enable_calendar"
              valuePropName="checked"
              extra="Si está encendido, el bot ya puede agendar con estos servicios al crearlo."
            >
              <Switch />
            </Form.Item>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
          <Button htmlType="button" disabled={step === 0 && personaQuiz === 0} onClick={goBack}>
            Atrás
          </Button>
          {step < STEPS.length - 1 ? (
            <Button key="next" type="primary" htmlType="button" onClick={goNext}>
              Siguiente
            </Button>
          ) : (
            <Button key="save" type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} disabled={!canSave}>
              {isEdit ? "Guardar cambios" : "Crear bot"}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  )

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/bots')}>
          Volver
        </Button>
        <h2 style={{ margin: 0 }}>{isEdit ? "Configuración del bot" : "Nuevo bot"}</h2>
      </div>

      {isEdit ? (
        <Tabs
          defaultActiveKey="config"
          items={[
            { key: 'config', label: 'Configuración', children: formContent },
            { key: 'apps', label: 'Aplicaciones', children: <BotAppsPanel botId={id!} /> },
          ]}
        />
      ) : (
        formContent
      )}
    </div>
  );
};

export default BotForm;
